import { asObject } from 'core/api/unwrap';

export const PROFILE_VALUE_FIELDS = [
  'full_name',
  'age_range',
  'education_level',
  'is_employed',
  'work_experience',
  'experience_field',
  'skills',
  'country',
];

export const PROFILE_PATCH_FIELDS = [
  'full_name',
  'preferred_locale',
  ...PROFILE_VALUE_FIELDS,
];

const PROTECTED_PROFILE_FIELDS = [
  'id',
  'phone',
  'email',
  'role',
  'first_name',
  'last_name',
  'legal',
  'profile_complete',
  'next_step',
  'phone_verified_at',
  'access_token',
  'refresh_token',
  'token_type',
  'expires_in',
  'refresh_expires_in',
];

export function normalizeOptionalBoolean(value) {
  if (value === true || value === 1) {
    return true;
  }
  if (value === false || value === 0) {
    return false;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase();
    if (trimmed === 'true' || trimmed === '1' || trimmed === 'yes') {
      return true;
    }
    if (trimmed === 'false' || trimmed === '0' || trimmed === 'no') {
      return false;
    }
  }
  return null;
}

export function uniqueSkills(items) {
  const seen = new Set();
  const next = [];
  (Array.isArray(items) ? items : []).forEach((item) => {
    const skill = String(item == null ? '' : item).trim();
    if (!skill) {
      return;
    }
    const key = skill.toLocaleLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    next.push(skill);
  });
  return next;
}

export function parseSkillTokens(raw) {
  return uniqueSkills(String(raw || '').split(/[,،]/));
}

export function normalizeSkills(value) {
  if (value == null || value === '') {
    return [];
  }
  if (Array.isArray(value)) {
    return uniqueSkills(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return uniqueSkills(parsed);
        }
      } catch {
        // Fall through to token split; a JSON array is the stored contract.
      }
    }
    return parseSkillTokens(trimmed);
  }
  return [];
}

export function normalizeOptionCode(value) {
  if (value == null) {
    return null;
  }
  const code = String(value).trim();
  return code === '' ? null : code;
}

export function adaptUser(raw) {
  if (raw == null) {
    return null;
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return raw;
  }
  const source = asObject(raw);
  const name = String(source.name || source.full_name || '').trim();
  return {
    ...source,
    name: name || (source.name == null ? '' : String(source.name)),
    full_name: source.full_name == null ? (name || null) : String(source.full_name),
    age_range: normalizeOptionCode(source.age_range),
    education_level: normalizeOptionCode(source.education_level),
    is_employed: normalizeOptionalBoolean(source.is_employed),
    work_experience: normalizeOptionCode(source.work_experience),
    experience_field: source.experience_field == null || String(source.experience_field).trim() === ''
      ? null
      : String(source.experience_field).trim(),
    skills: normalizeSkills(source.skills),
    country: normalizeOptionCode(source.country),
  };
}

export function adaptAuthSession(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return payload;
  }
  if (payload.user) {
    return {
      ...payload,
      user: adaptUser(payload.user),
    };
  }
  return payload;
}

export function sanitizeProfilePatch(body) {
  const source = asObject(body);
  const next = {};
  PROFILE_PATCH_FIELDS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      next[key] = source[key];
    }
  });
  PROTECTED_PROFILE_FIELDS.forEach((key) => {
    delete next[key];
  });
  if (Object.prototype.hasOwnProperty.call(next, 'is_employed')) {
    const employed = normalizeOptionalBoolean(next.is_employed);
    if (employed == null) {
      delete next.is_employed;
    } else {
      next.is_employed = employed;
    }
  }
  if (Object.prototype.hasOwnProperty.call(next, 'skills')) {
    next.skills = normalizeSkills(next.skills);
  }
  ['age_range', 'education_level', 'work_experience', 'country'].forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(next, key)) {
      next[key] = normalizeOptionCode(next[key]);
    }
  });
  if (Object.prototype.hasOwnProperty.call(next, 'full_name') && next.full_name != null) {
    next.full_name = String(next.full_name).trim();
  }
  if (Object.prototype.hasOwnProperty.call(next, 'experience_field') && next.experience_field != null) {
    next.experience_field = String(next.experience_field).trim();
  }
  return next;
}
